import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  ColDef,
  GridApi,
  GridReadyEvent,
  CellValueChangedEvent,
  GetRowIdParams,
  ICellRendererParams,
  RowClassRules,
  ValueFormatterParams,
  ValueGetterParams,
  ValueSetterParams,
} from 'ag-grid-community';
import { AlertTriangle, CheckCircle2, Clock, Download, ListPlus, Plus, Save, Search, Trash2, X } from 'lucide-react';
import { LopAgGrid } from '../../components/LopAgGrid';
import { LopButton } from '../../components/LopButton';
import { LopFilePicker } from '../../components/LopFilePicker';
import { LopSearchModal } from '../../components/LopSearchModal';
import { showErrorToast } from '../../components/ErrorToast';
import { useAuth } from '../../contexts/AuthContext';
import { useFunds } from '../../hooks/useFunds';
import { useBalanceTypes } from '../../hooks/useBalanceTypes';
import { useTabDirty } from '../../tabs';
import { canEdit as canEditUtil } from '../../utils/permissions';
import { dateColumnProps } from '../../utils/dateColumn';
import { getBankAccounts } from '../../api/bankAccounts';
import { downloadManualBalancesSample, getManualBalances, importManualBalances, saveManualBalances } from '../../api/manualBalances';
import type { BankAccount } from '../../types/BankAccount';
import type {
  ManualBalanceInput,
  ManualBalanceRow,
  ManualBalanceSaveResponse,
  ManualBalanceSummary,
} from '../../types/ManualBalance';
import { t } from '../../i18n/t';
import './ManualBalancesPage.css';
import '../DayOpen/ValuationParamsModal.css';

const RESOURCE_CODE = 'manualBalances4Valuation';

// Grid row = server row + client bookkeeping. __edited = touched/added since the last load/save,
// so the server-computed status no longer applies until the next save.
type GridRow = Omit<ManualBalanceRow, 'balanceTypeId'> & {
  balanceTypeId: number | null;
  __id: string;
  __edited?: boolean;
};

interface GridContext {
  canEdit: boolean;
  onDelete: (id: string) => void;
  onPickAsset: (id: string) => void;
}

const fmtNumber = (p: ValueFormatterParams<GridRow>) =>
  p.value == null || p.value === '' ? '' : Number(p.value).toLocaleString('he-IL', { maximumFractionDigits: 6 });

function StatusCell(p: ICellRendererParams<GridRow>) {
  const d = p.data;
  if (!d) return null;
  if (d.__edited) {
    return (
      <span className="mb-badge mb-badge--pending">
        <Clock size={14} aria-hidden="true" />
        {t('manualBalances.status.pending')}
      </span>
    );
  }
  if (d.isValid) {
    return (
      <span className="mb-badge mb-badge--ok">
        <CheckCircle2 size={14} aria-hidden="true" />
        {t('manualBalances.status.ok')}
      </span>
    );
  }
  return (
    <span className="mb-badge mb-badge--bad" title={d.rowStatus}>
      <AlertTriangle size={14} aria-hidden="true" />
      {d.rowStatus}
    </span>
  );
}

function AssetCell(p: ICellRendererParams<GridRow>) {
  const ctx = p.context as GridContext;
  const d = p.data;
  if (!d) return null;
  const label = d.assetId ? `${d.assetId}${d.assetName ? ` - ${d.assetName}` : ''}` : '';
  return (
    <span className="mb-asset">
      <span className="mb-asset__label">{label}</span>
      {ctx.canEdit && (
        <button
          type="button"
          className="mb-asset__pick"
          aria-label={t('manualBalances.searchAsset')}
          title={t('manualBalances.searchAsset')}
          onClick={() => ctx.onPickAsset(d.__id)}
        >
          <Search size={14} />
        </button>
      )}
    </span>
  );
}

function ActionsCell(p: ICellRendererParams<GridRow>) {
  const ctx = p.context as GridContext;
  const d = p.data;
  if (!d || !ctx.canEdit) return null;
  return (
    <button
      type="button"
      className="mb-delete"
      aria-label={t('manualBalances.deleteRow')}
      title={t('manualBalances.deleteRow')}
      onClick={() => ctx.onDelete(d.__id)}
    >
      <Trash2 size={16} />
    </button>
  );
}

let idSeq = 0;
const nextId = () => `mb-${++idSeq}`;

function toGridRows(rows: ManualBalanceRow[]): GridRow[] {
  return rows.map((r) => ({ ...r, __id: nextId(), __edited: false }));
}

function toInput(r: GridRow): ManualBalanceInput {
  return {
    acc: r.acc.trim(),
    assetId: r.assetId,
    balanceTypeId: r.balanceTypeId as number,
    qty: r.qty ?? 0,
    qty4Receive: r.qty4Receive ?? null,
    expirationDate: r.expirationDate || null,
    executionDate: r.executionDate || null,
    remark: r.remark?.trim() ? r.remark : null,
    enteredBy: r.enteredBy?.trim() ? r.enteredBy : null,
    isTransferInProcess: !!r.isTransferInProcess,
  };
}

function errMessage(err: unknown): string {
  const e = err as { serverMessage?: string | null; message?: string };
  return e?.serverMessage ?? e?.message ?? String(err);
}

export function ManualBalancesPage() {
  // Tenant-scoped: useFunds self-sufficiently sets X-Tenant-Id before the first fetch.
  const { data: funds, loading: fundsLoading, error: fundsError } = useFunds();
  const { data: balanceTypes } = useBalanceTypes();
  const { user } = useAuth();
  const canEdit = canEditUtil(user, RESOURCE_CODE);

  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [rows, setRows] = useState<GridRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [version, setVersion] = useState(0);
  const [onlyInvalid, setOnlyInvalid] = useState(false);
  const [summary, setSummary] = useState<{ kind: 'import' | 'save'; data: ManualBalanceSummary } | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [pickerFor, setPickerFor] = useState<string | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const gridApiRef = useRef<GridApi<GridRow> | null>(null);
  const rowsRef = useRef<GridRow[]>([]);
  rowsRef.current = rows;
  const busyRef = useRef(false);

  useTabDirty(dirty);

  // Browser-level guard (closing/reloading the tab) while there are unsaved edits.
  useEffect(() => {
    if (!dirty) return;
    const h = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [dirty]);

  const applyResponse = useCallback((res: ManualBalanceSaveResponse, kind: 'import' | 'save') => {
    setRows(toGridRows(res.rows));
    setDirty(false);
    setSummary({ kind, data: res.summary });
    setActionError(null);
    setVersion((v) => v + 1);
  }, []);

  useEffect(() => {
    if (fundsLoading || fundsError) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([getManualBalances(), getBankAccounts()])
      .then(([data, accs]) => {
        if (cancelled) return;
        setRows(toGridRows(data));
        setAccounts(accs);
        setLoadError(null);
      })
      .catch((err: unknown) => {
        if (!cancelled) setLoadError(errMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [fundsLoading, fundsError]);

  const fundNameById = useMemo(() => new Map((funds ?? []).map((f) => [f.fundId, f.fundName])), [funds]);
  const fundIdByAcc = useMemo(() => new Map(accounts.map((a) => [a.acc, a.fundId])), [accounts]);
  const accValues = useMemo(() => accounts.map((a) => a.acc), [accounts]);
  const btLabelById = useMemo(
    () => new Map((balanceTypes ?? []).map((b) => [b.balanceTypeId, `${b.balanceTypeId} - ${b.balanceTypeName}`])),
    [balanceTypes],
  );
  const btIdByLabel = useMemo(
    () => new Map((balanceTypes ?? []).map((b) => [`${b.balanceTypeId} - ${b.balanceTypeName}`, b.balanceTypeId])),
    [balanceTypes],
  );
  const btValues = useMemo(() => Array.from(btLabelById.values()), [btLabelById]);

  const counts = useMemo(() => {
    void version;
    const valid = rows.filter((r) => !r.__edited && r.isValid).length;
    const invalid = rows.filter((r) => !r.__edited && !r.isValid).length;
    return { total: rows.length, valid, invalid };
  }, [rows, version]);

  const displayRows = useMemo(
    () => (onlyInvalid ? rows.filter((r) => r.__edited || !r.isValid) : rows),
    [rows, onlyInvalid],
  );

  const handleDelete = useCallback((id: string) => {
    setRows((prev) => prev.filter((r) => r.__id !== id));
    setDirty(true);
  }, []);
  const handlePickAsset = useCallback((id: string) => setPickerFor(id), []);

  const context = useMemo<GridContext>(
    () => ({ canEdit, onDelete: handleDelete, onPickAsset: handlePickAsset }),
    [canEdit, handleDelete, handlePickAsset],
  );

  const columnDefs = useMemo<ColDef<GridRow>[]>(
    () => [
      {
        colId: 'status',
        headerName: t('manualBalances.columns.status'),
        pinned: 'left',
        width: 230,
        editable: false,
        sortable: false,
        filter: false,
        cellRenderer: StatusCell,
        valueGetter: (p: ValueGetterParams<GridRow>) =>
          p.data?.__edited ? t('manualBalances.status.pending') : p.data?.isValid ? t('manualBalances.status.ok') : p.data?.rowStatus,
      },
      {
        field: 'acc',
        headerName: t('manualBalances.columns.acc'),
        width: 150,
        editable: canEdit,
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: { values: accValues },
      },
      {
        colId: 'fundName',
        headerName: t('manualBalances.columns.fundName'),
        width: 260,
        editable: false,
        valueGetter: (p: ValueGetterParams<GridRow>) => {
          const d = p.data;
          if (!d) return '';
          if (!d.__edited) return d.fundName ?? '';
          const fid = fundIdByAcc.get(d.acc);
          return fid != null ? (fundNameById.get(fid) ?? '') : '';
        },
      },
      {
        field: 'assetId',
        headerName: t('manualBalances.columns.assetId'),
        width: 380,
        editable: canEdit,
        cellDataType: 'number',
        cellRenderer: AssetCell,
        valueSetter: (p: ValueSetterParams<GridRow>) => {
          if (!p.data) return false;
          const v = Number(p.newValue);
          if (!Number.isFinite(v) || v === p.data.assetId) return false;
          p.data.assetId = v;
          p.data.assetName = null; // name is re-read from the server on save
          return true;
        },
      },
      {
        field: 'balanceTypeId',
        headerName: t('manualBalances.columns.balanceTypeId'),
        width: 200,
        editable: canEdit,
        cellDataType: 'text',
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: { values: btValues },
        valueGetter: (p: ValueGetterParams<GridRow>) => {
          const id = p.data?.balanceTypeId;
          if (id == null) return '';
          return btLabelById.get(id) ?? String(id);
        },
        valueSetter: (p: ValueSetterParams<GridRow>) => {
          if (!p.data) return false;
          const id = btIdByLabel.get(String(p.newValue));
          if (id == null) return false;
          p.data.balanceTypeId = id;
          return true;
        },
      },
      {
        field: 'qty',
        headerName: t('manualBalances.columns.qty'),
        width: 140,
        editable: canEdit,
        cellDataType: 'number',
        valueFormatter: fmtNumber,
      },
      {
        field: 'qty4Receive',
        headerName: t('manualBalances.columns.qty4Receive'),
        width: 140,
        editable: canEdit,
        cellDataType: 'number',
        valueFormatter: fmtNumber,
      },
      {
        field: 'expirationDate',
        headerName: t('manualBalances.columns.expirationDate'),
        width: 150,
        editable: canEdit,
        ...dateColumnProps<GridRow>('expirationDate'),
      },
      {
        field: 'executionDate',
        headerName: t('manualBalances.columns.executionDate'),
        width: 150,
        editable: canEdit,
        ...dateColumnProps<GridRow>('executionDate'),
      },
      { field: 'remark', headerName: t('manualBalances.columns.remark'), width: 220, editable: canEdit },
      { field: 'enteredBy', headerName: t('manualBalances.columns.enteredBy'), width: 140, editable: canEdit },
      {
        field: 'isTransferInProcess',
        headerName: t('manualBalances.columns.isTransferInProcess'),
        width: 150,
        editable: canEdit,
        cellDataType: 'boolean',
      },
      {
        colId: 'actions',
        headerName: t('manualBalances.columns.actions'),
        pinned: 'right',
        width: 90,
        editable: false,
        sortable: false,
        filter: false,
        resizable: false,
        cellRenderer: ActionsCell,
        hide: !canEdit,
      },
    ],
    [canEdit, accValues, fundIdByAcc, fundNameById, btValues, btLabelById, btIdByLabel],
  );

  const defaultColDef = useMemo<ColDef>(() => ({ flex: undefined, minWidth: 80 }), []);
  const getRowId = useCallback((p: GetRowIdParams<GridRow>) => p.data.__id, []);
  const rowClassRules = useMemo<RowClassRules<GridRow>>(
    () => ({
      'mb-row-invalid': (p) => !!p.data && !p.data.__edited && !p.data.isValid,
      'mb-row-edited': (p) => !!p.data?.__edited,
    }),
    [],
  );
  const onGridReady = useCallback((e: GridReadyEvent<GridRow>) => {
    gridApiRef.current = e.api;
  }, []);
  const onCellValueChanged = useCallback((e: CellValueChangedEvent<GridRow>) => {
    if (e.data) e.data.__edited = true;
    setDirty(true);
    setVersion((v) => v + 1);
    e.api.refreshCells({ rowNodes: [e.node], force: true });
  }, []);

  const handleAdd = useCallback(() => {
    const row: GridRow = {
      recId: 0,
      acc: '',
      assetId: 0,
      balanceTypeId: null,
      qty: 0,
      qty4Receive: null,
      expirationDate: null,
      executionDate: null,
      remark: null,
      enteredBy: null,
      isTransferInProcess: false,
      createdAt: '',
      createdBy: null,
      assetName: null,
      balanceTypeName: null,
      fundId: null,
      fundName: null,
      accKnown: false,
      assetKnown: false,
      balanceTypeKnown: false,
      isValid: false,
      rowStatus: '',
      __id: nextId(),
      __edited: true,
    };
    setOnlyInvalid(false);
    setRows((prev) => [row, ...prev]);
    setDirty(true);
  }, []);

  const handleSave = useCallback(async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    setSaving(true);
    try {
      const current = rowsRef.current;
      const bad = current.findIndex((r) => !r.acc.trim() || !(r.assetId > 0) || r.balanceTypeId == null);
      if (bad >= 0) {
        setActionError(t('manualBalances.errIncomplete', { n: bad + 1 }));
        return;
      }
      const res = await saveManualBalances(current.map(toInput));
      applyResponse(res, 'save');
    } catch (err: unknown) {
      showErrorToast(err, { title: t('errorToast.titleSave') });
    } finally {
      busyRef.current = false;
      setSaving(false);
    }
  }, [applyResponse]);

  const handleFilePicked = useCallback((picked: File[]) => {
    setFiles(picked);
    setPendingFile(picked[0] ?? null);
  }, []);
  const cancelImport = useCallback(() => {
    setPendingFile(null);
    setFiles([]);
  }, []);

  const confirmImport = useCallback(async () => {
    const file = pendingFile;
    if (!file || busyRef.current) return;
    busyRef.current = true;
    setImporting(true);
    setPendingFile(null);
    try {
      const res = await importManualBalances(file);
      applyResponse(res, 'import');
    } catch (err: unknown) {
      setActionError(errMessage(err));
      setSummary(null);
    } finally {
      busyRef.current = false;
      setImporting(false);
      setFiles([]);
    }
  }, [pendingFile, applyResponse]);

  // Esc closes the confirm dialog.
  useEffect(() => {
    if (!pendingFile) return;
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') cancelImport();
    };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [pendingFile, cancelImport]);

  const assetColumns = useMemo(
    () => [
      { field: 'assetId', headerName: t('assets.columns.assetId') },
      { field: 'assetName', headerName: t('assets.columns.assetName') },
      { field: 'symbol', headerName: t('assets.columns.symbol') },
    ],
    [],
  );
  const closePicker = useCallback(() => setPickerFor(null), []);
  const onAssetSelected = useCallback(
    (row: Record<string, unknown>) => {
      const picked = row.assetId;
      const id = pickerFor;
      if (typeof picked !== 'number' || !id) return;
      const target = rowsRef.current.find((r) => r.__id === id);
      if (!target) return;
      target.assetId = picked;
      target.assetName = typeof row.assetName === 'string' ? row.assetName : null;
      target.__edited = true;
      setDirty(true);
      setVersion((v) => v + 1);
      const node = gridApiRef.current?.getRowNode(id);
      if (node) gridApiRef.current?.refreshCells({ rowNodes: [node], force: true });
    },
    [pickerFor],
  );

  if (fundsError || loadError) {
    return (
      <div className="mb-page">
        <div className="mb-banner mb-banner--error" role="alert">
          {t('manualBalances.errorPrefix', { message: fundsError ?? loadError ?? '' })}
        </div>
      </div>
    );
  }
  if (fundsLoading) return <div className="mb-page mb-loading">{t('common.loading')}</div>;

  const busy = saving || importing;
  const s = summary?.data;

  return (
    <div className="mb-page">
      <header className="mb-header">
        <h1 className="mb-title">
          <ListPlus size={22} aria-hidden="true" />
          {t('manualBalances.pageTitle')}
        </h1>
        <div className="mb-counts" id="mb-counts" aria-live="polite">
          <span className="mb-count mb-count--total">{t('manualBalances.counts.total', { n: counts.total })}</span>
          <span className="mb-count mb-count--ok">
            <CheckCircle2 size={14} aria-hidden="true" />
            {t('manualBalances.counts.valid', { n: counts.valid })}
          </span>
          <span className="mb-count mb-count--bad">
            <AlertTriangle size={14} aria-hidden="true" />
            {t('manualBalances.counts.invalid', { n: counts.invalid })}
          </span>
        </div>
      </header>

      <div className="mb-toolbar">
        {canEdit && (
          <div className="mb-picker">
            <LopFilePicker
              id="mb-file"
              files={files}
              onChange={handleFilePicked}
              accept=".xlsm,.xlsx"
              disabled={busy}
              title={t('manualBalances.importFile')}
              hint={t('manualBalances.importHint')}
              replaceHint={t('manualBalances.importReplaceHint')}
              clearLabel={t('manualBalances.importClear')}
            />
          </div>
        )}
        <LopButton
          id="mb-sample-link"
          variant="link"
          icon={<Download size={14} />}
          onClick={() => {
            downloadManualBalancesSample().catch((err) =>
              showErrorToast(err, { title: t('manualBalances.sampleError') }));
          }}
        >
          {t('manualBalances.sample')}
        </LopButton>
        {canEdit && (
          <LopButton id="mb-add" icon={<Plus size={16} />} onClick={handleAdd} disabled={busy || loading}>
            {t('manualBalances.addRow')}
          </LopButton>
        )}
        {canEdit && (
          <LopButton
            id="mb-save"
            variant="primary"
            icon={<Save size={16} />}
            onClick={handleSave}
            loading={saving}
            disabled={!dirty || importing}
          >
            {t('manualBalances.save')}
          </LopButton>
        )}
        <label className="mb-filter">
          <input
            id="mb-only-invalid"
            type="checkbox"
            checked={onlyInvalid}
            onChange={(e) => setOnlyInvalid(e.target.checked)}
          />
          {t('manualBalances.onlyInvalid')}
        </label>
      </div>

      {importing && <div className="mb-banner mb-banner--info" role="status">{t('common.loading')}</div>}
      {dirty && canEdit && <div className="mb-banner mb-banner--warn" role="status">{t('manualBalances.unsavedWarning')}</div>}
      {s && !actionError && (
        <div className="mb-banner mb-banner--ok" id="mb-summary" role="status">
          <CheckCircle2 size={16} aria-hidden="true" />
          {summary?.kind === 'import'
            ? t('manualBalances.summary', {
                sheetName: s.sheetName ?? '',
                rowsRead: s.rowsRead ?? s.total,
                skipped: s.rowsSkippedEmpty ?? 0,
                valid: s.valid,
                invalid: s.invalid,
                pushed: s.pushedToInventory,
              })
            : t('manualBalances.summarySave', {
                total: s.total,
                valid: s.valid,
                invalid: s.invalid,
                pushed: s.pushedToInventory,
              })}
        </div>
      )}
      {actionError && (
        <div className="mb-banner mb-banner--error" id="mb-error" role="alert">
          <AlertTriangle size={16} aria-hidden="true" />
          <span className="mb-error__text">{t('manualBalances.importError')}: {actionError}</span>
          <button type="button" className="mb-banner__close" aria-label={t('recordForm.close')} onClick={() => setActionError(null)}>
            <X size={14} />
          </button>
        </div>
      )}

      <div className="mb-grid">
        <LopAgGrid<GridRow>
          rowData={displayRows}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          getRowId={getRowId}
          rowClassRules={rowClassRules}
          context={context}
          onGridReady={onGridReady}
          onCellValueChanged={onCellValueChanged}
          loading={loading}
          stopEditingWhenCellsLoseFocus={true}
        />
      </div>

      {pendingFile && (
        <div
          className="vparams__backdrop"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) cancelImport();
          }}
        >
          <div className="vparams" role="alertdialog" aria-modal="true" aria-label={t('manualBalances.confirmTitle')} id="mb-confirm">
            <header className="vparams__header">
              <h2 className="vparams__title">{t('manualBalances.confirmTitle')}</h2>
              <button type="button" className="vparams__close" aria-label={t('recordForm.close')} onClick={cancelImport}>
                <X size={18} />
              </button>
            </header>
            <div className="mb-confirm__body">
              <p className="mb-confirm__text">{t('manualBalances.confirmText', { count: rows.length })}</p>
              <p className="mb-confirm__file" dir="ltr">{pendingFile.name}</p>
            </div>
            <footer className="vparams__footer">
              <LopButton variant="danger" id="mb-confirm-proceed" onClick={confirmImport}>
                {t('manualBalances.confirmProceed')}
              </LopButton>
              <LopButton variant="secondary" id="mb-confirm-cancel" onClick={cancelImport}>
                {t('manualBalances.confirmCancel')}
              </LopButton>
            </footer>
          </div>
        </div>
      )}

      {pickerFor && (
        <LopSearchModal
          title={t('manualBalances.searchAsset')}
          table="ASSETS"
          columns={assetColumns}
          onSelect={onAssetSelected}
          onClose={closePicker}
        />
      )}
    </div>
  );
}
